import { Product } from '../services/productService';

/**
 * Generates the ZPL code for a product label.
 * Verified configuration:
 * - Label Size: ~10cm x 3cm (approx) -> ^PW812 (4") ^LL240 (1.2")
 * - QR Code: Magnification 4, Error Correction standard (QA), Position aligned (20,20)
 * - Text: Aligned with QR top margin
 */

interface ZplOptions {
    noteNumber?: string | null;
    availableSizes?: string[];
    availableColors?: string[];
}

export const generateProductLabelZPL = (product: Product, options: ZplOptions = {}): string => {
    const { noteNumber, availableSizes = [], availableColors = [] } = options;

    // 1. Prepare Data
    let qrValue = product.qr_code_value;
    if (!qrValue) {
        const sanitize = (str: string | null | undefined) => str ? str.toUpperCase().replace(/[^A-Z0-9]/g, '') : '';
        const p1 = sanitize(product.cod_producto);
        const p2 = sanitize(product.color || '');
        let p3 = sanitize(product.codigo_proveedor || '');
        if (!p3 && product.marca) {
            p3 = sanitize(product.marca.substring(0, 3));
        }
        if (!p3) p3 = 'GEN';
        const p4 = sanitize(product.talla || '');
        qrValue = `${p1}-${p2}-${p3}-${p4}`;
    }

    const isSold = product.pre_order || !!noteNumber;

    // Date Formatting (CDMX)
    const dateObj = product.received_at ? new Date(product.received_at) : new Date();
    const dateStr = dateObj.toLocaleDateString('es-MX', {
        timeZone: 'America/Mexico_City',
        day: '2-digit', month: '2-digit', year: '2-digit'
    });

    // Variant Formatting
    let sizesStr = '';
    if (availableSizes.length > 0) {
        const numericSizes = availableSizes.map(s => parseFloat(s)).filter(n => !isNaN(n)).sort((a, b) => a - b);
        if (numericSizes.length === availableSizes.length && numericSizes.length > 1) {
            sizesStr = `${numericSizes[0]}-${numericSizes[numericSizes.length - 1]}`;
        } else {
            sizesStr = availableSizes.slice(0, 6).join(' ');
        }
    }

    const colorsStr = availableColors.slice(0, 3).map(c => c.substring(0, 10).toUpperCase()).join(' ');

    // Layout ZPL (4" x 1.2" = 812 x 240 dots)
    // Left: QR + Date
    // Center: REF, MOD, TALLA (Main), COLOR
    // Right: PRICE (Big), Table (Sizes/Colors)

    // Dynamic QR Magnification and Date Position to prevent overlap
    // Model 2 V2 (25x25) holds ~20 chars. V3 (29x29) holds ~35.
    // Mag 4: V3=116 dots. V2=100 dots. Label Height limit ~120 dots for QR.
    // FORCE Mag 3 for all labels to ensure safety margin as per user feedback (all labels overlapping).
    // UPDATE: User reported Mag 3 is too small to scan. Reverting to Mag 4.
    // To prevent overlap, we push date further down and reduce its font slightly.
    const qrMag = 4;
    const qrY = 18;
    const dateY = 145;

    // Dynamic Font for MOD field to prevent overlap with Price/Right panel
    // Area width ~330 dots. "MOD: " + code.
    const fullModStr = `MOD: ${product.cod_producto || ''}`;
    let modFontH = 25;
    let modFontW = 25;
    if (fullModStr.length > 15) {
        modFontH = 20;
        modFontW = 20;
    }
    if (fullModStr.length > 20) {
        modFontH = 18;
        modFontW = 18;
    }

    return `
^XA
^BY2,2,0
^LT0^LH0,0
^PW799
^LL162

^FO25,${qrY}^BQN,2,${qrMag}^FDQA,${qrValue}^FS
^FO25,${dateY}^A0N,15,15^FD${dateStr}^FS

^FO220,15^A0N,20,25^FDREF: ${product.codigo_proveedor || 'N/A'}^FS
^FO220,38^A0N,${modFontH},${modFontW}^FD${fullModStr}^FS
^FO220,63^A0N,55,50^FD${product.talla || ''}^FS
^FO220,120^A0N,25,25^FD${product.color || ''}^FS

${isSold
            ? `
^FO550,30^A0N,30,30^FD${noteNumber || 'VENDIDO'}^FS
^FO550,70^A0N,20,20^FD${(noteNumber && noteNumber.startsWith('TNL-')) ? 'VENTA EN LINEA' : 'VENDIDO'}^FS
`
            : `

${product.rebajado
                ? `^FO550,15^A0N,20,20^FDREBAJADO^FS
       ^FO550,40^A0N,65,60^FD$${product.precio || '0'}^FS`
                : `^FO550,25^A0N,80,80^FD$${product.precio || '0'}^FS`
            }

`}

^FO550,100^GB240,0,1^FS
^FO550,110^A0N,18,18^FDTALLAS: ${sizesStr}^FS
^FO550,127^A0N,18,18^FDCOLORES: ${colorsStr}^FS

^XZ
    `;
};
